import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { MDBModalService } from '../mdb-compat';
import { ModalInfoComponent } from '../modal-info/modal-info.component';
import { hideInlineLoader } from '../_actions/inline-loader.actions';
import { guest } from '../_actions/app-flow-state.actions';
import { clearCart } from '../_actions/cart.actions';
import { resetRates } from '../_actions/rates.actions';
import { removeVehicle } from '../_actions/vehicle.actions';
import { resetDealer } from '../_actions/dealer-actions';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SDLogoutActivateGuard implements CanActivate, CanActivateChild {

  constructor(
    private store: Store<{}>,
    private modalService: MDBModalService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    const appFlowState = sessionStorage.getItem('appFlowState');
    
    // Only intercept if in SDLogout state
    if (appFlowState !== 'SDLogout') {
      return true;
    }

    // Never show modal when navigating to service-drive or products (allow through without prompt)
    const targetUrl = state.url.split('?')[0];
    if (targetUrl === '/service-drive' || targetUrl.startsWith('/service-drive/')) {
      return true;
    }
    if (targetUrl === '/products' || targetUrl.startsWith('/products/')) {
      return true;
    }

    // Hide inline loader immediately when intercepting navigation in SDLogout mode
    // This prevents components from showing it during navigation
    this.store.dispatch(hideInlineLoader());

    // Show confirmation modal
    const modalOptions = {
      backdrop: true,
      keyboard: true,
      focus: true,
      show: false,
      scroll: true,
      ignoreBackdropClick: true,
      class: "modal-dialog-centered",
      containerClass: "",
      animated: true,
      data: {
        title: "Continue Navigation?",
        content: "You are currently in Service Drive logout mode. Do you want to continue to this page?",
        decisionMode: true,
        confirmButtonText: "Yes, Continue",
        cancelButtonText: "Cancel",
      },
    };

    const modalRef = this.modalService.show(ModalInfoComponent, modalOptions);
    
    return new Promise<boolean>((resolve) => {
      const actionSubscription = modalRef?.content?.action?.subscribe((action: string) => {
        if (action === "confirm") {
          modalRef.hide();
          // Clear cart and related data, then transition to guest state
          this.store.dispatch(clearCart());
          this.store.dispatch(resetRates());
          this.store.dispatch(removeVehicle());
          this.store.dispatch(resetDealer());
          this.store.dispatch(guest());
          actionSubscription.unsubscribe();
          resolve(true);
        } else if (action === "cancel") {
          modalRef.hide();
          // Hide inline loader immediately when user cancels navigation
          this.store.dispatch(hideInlineLoader());
          actionSubscription.unsubscribe();
          resolve(false);
        }
      });
    });
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.canActivate(childRoute, state);
  }
}
